import { computed, isReactive, type Readable, effect as rawEffect, untrack } from '@vielzeug/ripple';

import { invariant } from '../errors';
import { createDirectiveResult, type DirectiveResult, type HTMLResult, isHtmlResult } from '../template/result';
import { createReplaceableSlot, removeNodes } from '../utils/dom';

type MaybeReactive<T> = T | (() => T) | Readable<T>;

type WhenRenderable = HTMLResult | null | undefined | false;

const NO_PARENT_MSG = 'when() anchor comment has no parent node';

/**
 * Conditionally renders one of two branches as a `DirectiveResult`.
 *
 * The condition may be a boolean, signal, or getter function.
 * When the condition changes, the current branch is cleaned up and
 * the new branch is mounted in its place.
 *
 * @example
 * ```ts
 * html`${when(isLoggedIn, () => html`<p>Welcome</p>`, () => html`<p>Log in</p>`)}`
 * ```
 */
export function when(
  condition: MaybeReactive<boolean>,
  truthy: () => WhenRenderable,
  falsy?: () => WhenRenderable,
): DirectiveResult {
  if (typeof condition !== 'function' && !isReactive(condition)) {
    return createDirectiveResult((anchor, registerCleanup) => {
      const branch = condition ? truthy() : falsy ? falsy() : null;

      if (!branch || !isHtmlResult(branch)) return;

      const parent = anchor.parentNode;

      invariant(parent, NO_PARENT_MSG);

      const nodes = branch.mount(parent, anchor, registerCleanup);

      registerCleanup(() => removeNodes(nodes));
    });
  }

  return createDirectiveResult((anchor, registerCleanup) => {
    const ownedComputed = typeof condition === 'function' ? computed(condition as () => boolean) : null;
    const conditionSignal = ownedComputed ?? (condition as Readable<boolean>);

    const parent = anchor.parentNode;

    invariant(parent, NO_PARENT_MSG);

    const endMarker = document.createComment('when/end');

    parent.insertBefore(endMarker, anchor.nextSibling);

    const slot = createReplaceableSlot();

    const sub = rawEffect(() => {
      const next = conditionSignal.value;

      slot.clear();

      const branch = next ? truthy() : falsy ? falsy() : null;

      if (!branch || !isHtmlResult(branch)) return;

      slot.setNodes(untrack(() => branch.mount(parent, endMarker, slot.registerCleanup)));
    });

    registerCleanup(() => sub.dispose());
    registerCleanup(() => {
      slot.clear();
      endMarker.remove();
    });
  });
}
